import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { 
  LogOut, BookOpen, Award, Clock, CheckCircle, Volume2, Calendar, 
  ChevronDown, ChevronRight, ChevronLeft, Home, Headphones, Bell, Target, Trophy,
  BarChart3, FileText, Users, HelpCircle, Play, Search, X, Download,
  Timer, AlertCircle, Lock, Unlock, Heart, Smile, Meh, Frown, AlertTriangle,
  MessageCircle, Brain, Mic, TrendingUp, TrendingDown, Minus, Filter,
  ArrowUpRight, ArrowDownRight, Zap, Star, BookMarked, GraduationCap, Lightbulb, RefreshCw, Route, Menu, Sparkles, Wallet
} from 'lucide-react';
import { format, subDays, isWithinInterval, startOfWeek, endOfWeek } from 'date-fns';
import StudentMockExams from './StudentMockExams';
import SkillAssessment from './SkillAssessment';
import PracticeMock from './PracticeMock';
import AudioLearning from './AudioLearning';
import DoubtSolver from '@/components/DoubtSolver';
import HolidayCalendar from '@/components/HolidayCalendar';
import DailyMotivation from '@/components/DailyMotivation';
import StudentFeesView from '@/components/StudentFeesView';
import StudentBadges from '@/components/StudentBadges';
import StudentProgressWidget from '@/components/StudentProgressWidget';
import ExamReadinessGauge from '@/components/ExamReadinessGauge';
import WeakTopicsList from '@/components/WeakTopicsList';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sidebar Menu Item Component
const MenuItem = ({ icon: Icon, label, active, onClick, hasSubmenu, isOpen, children }) => {
  return (
    <div>
      <button
        onClick={onClick}
        className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${
          active 
            ? 'bg-teal-700 text-white' 
            : 'text-teal-100 hover:bg-teal-700/50'
        }`}
      >
        <div className="flex items-center gap-3">
          <Icon size={18} />
          <span className="font-medium text-sm uppercase tracking-wide">{label}</span>
        </div>
        {hasSubmenu && (
          <span className="text-teal-300">
            {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </span>
        )}
      </button>
      {hasSubmenu && isOpen && (
        <div className="bg-teal-900/50">
          {children}
        </div>
      )}
    </div>
  );
};

const SubMenuItem = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full px-8 py-2.5 text-left text-sm transition-colors ${
      active 
        ? 'bg-teal-600 text-white' 
        : 'text-teal-200 hover:bg-teal-700/30'
    }`}
  >
    {label}
  </button>
);

const NewStudentDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [activeSection, setActiveSection] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [studentOnlineClasses, setStudentOnlineClasses] = useState([]);
  useEffect(() => { setSidebarOpen(false); }, [activeSection]);

  useEffect(() => {
    if (activeSection === 'online-classes') {
      axios.get(`${API}/student/online-classes`).then(res => setStudentOnlineClasses(res.data)).catch(() => {});
    }
  }, [activeSection]);
  const [activeSubSection, setActiveSubSection] = useState(null);
  const [expandedMenus, setExpandedMenus] = useState({ progress: false, audio: false });
  
  // Data state
  const [availableTests, setAvailableTests] = useState([]);
  const [approvedResults, setApprovedResults] = useState([]);
  const [pendingResults, setPendingResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syllabus, setSyllabus] = useState(null);
  const [attendanceData, setAttendanceData] = useState({ percentage: 0, total_days: 0, present: 0, absent: 0 });
  const [myAssignments, setMyAssignments] = useState({ assignments: [], total: 0 });
  
  // Test taking state
  const [currentTest, setCurrentTest] = useState(null);
  const [currentAttempt, setCurrentAttempt] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionNumber, setQuestionNumber] = useState(1);
  const [userAnswer, setUserAnswer] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [savedAnswers, setSavedAnswers] = useState({}); // Store all answers for review
  const [allQuestions, setAllQuestions] = useState([]); // All questions for navigation
  const [showReview, setShowReview] = useState(false); // Review mode before submit
  
  // Filters
  const [filters, setFilters] = useState({
    subject: '',
    term: ''
  });

  // Mental Health state
  const [todayCheckin, setTodayCheckin] = useState(null);
  const [checkinHistory, setCheckinHistory] = useState([]);
  const [selectedMood, setSelectedMood] = useState(null);
  const [stressLevel, setStressLevel] = useState(3);
  const [mentalNote, setMentalNote] = useState('');
  const [requestToTalk, setRequestToTalk] = useState(false);
  const [submittingCheckin, setSubmittingCheckin] = useState(false);

  // Adaptive Learning State
  const [showPreExamAnalysis, setShowPreExamAnalysis] = useState(false);
  const [preExamAnalysis, setPreExamAnalysis] = useState(null);
  const [selectedTestForAnalysis, setSelectedTestForAnalysis] = useState(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [studyPlan, setStudyPlan] = useState(null);
  const [showPostExamRemedial, setShowPostExamRemedial] = useState(false);
  const [postExamRemedial, setPostExamRemedial] = useState(null);
  const [lastCompletedAttemptId, setLastCompletedAttemptId] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    if (currentTest && timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            handleSubmitTest();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [currentTest, timeRemaining]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [testsResponse, resultsResponse, pendingResponse, syllabusResponse, todayCheckinResponse, historyResponse, attendanceResponse, assignmentsResponse] = await Promise.all([
        axios.get(`${API}/student/tests/available`),
        axios.get(`${API}/student/results/approved`),
        axios.get(`${API}/student/results/pending`),
        axios.get(`${API}/student/syllabus?class_level=${user?.class_level || '6'}&subject=Mathematics`),
        axios.get(`${API}/student/mental-health/today`).catch(() => ({ data: { checked_in_today: false } })),
        axios.get(`${API}/student/mental-health/history?days=7`).catch(() => ({ data: [] })),
        axios.get(`${API}/student/attendance`).catch(() => ({ data: { percentage: 0, total_days: 0, present: 0, absent: 0 } })),
        axios.get(`${API}/student/my-assignments`).catch(() => ({ data: { assignments: [], total: 0 } })),
      ]);
      setAvailableTests(testsResponse.data);
      setApprovedResults(resultsResponse.data);
      setPendingResults(pendingResponse.data);
      setSyllabus(syllabusResponse.data);
      setTodayCheckin(todayCheckinResponse.data);
      setCheckinHistory(historyResponse.data);
      setAttendanceData(attendanceResponse.data);
      setMyAssignments(assignmentsResponse.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const submitMentalHealthCheckin = async () => {
    if (!selectedMood) {
      toast.error('Please select how you are feeling');
      return;
    }
    
    setSubmittingCheckin(true);
    try {
      await axios.post(`${API}/student/mental-health/checkin`, {
        mood: selectedMood,
        stress_level: stressLevel,
        note: mentalNote || null,
        request_to_talk: requestToTalk
      });
      
      toast.success('Check-in recorded! Thank you for sharing.');
      setSelectedMood(null);
      setStressLevel(3);
      setMentalNote('');
      setRequestToTalk(false);
      loadDashboardData();
    } catch (error) {
      console.error('Error submitting check-in:', error);
      toast.error('Failed to submit check-in');
    } finally {
      setSubmittingCheckin(false);
    }
  };

  const toggleMenu = (menu) => {
    setExpandedMenus(prev => ({ ...prev, [menu]: !prev[menu] }));
  };

  const handleLogout = () => {
    logout();
  };

  // Adaptive Learning Functions
  const loadPreExamAnalysis = async (test) => {
    setLoadingAnalysis(true);
    setSelectedTestForAnalysis(test);
    try {
      const response = await axios.get(`${API}/adaptive/mock/pre-exam-analysis/${test.id}`);
      setPreExamAnalysis(response.data);
      setShowPreExamAnalysis(true);
    } catch (error) {
      console.error('Error loading pre-exam analysis:', error);
      // If analysis fails, just start the test directly
      await startTestDirectly(test);
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const loadStudyPlan = async () => {
    try {
      const response = await axios.get(`${API}/adaptive/mock/study-plan`);
      setStudyPlan(response.data);
    } catch (error) {
      console.error('Error loading study plan:', error);
    }
  };

  const loadPostExamRemedial = async (attemptId) => {
    try {
      const response = await axios.get(`${API}/adaptive/mock/post-exam-remedial/${attemptId}`);
      setPostExamRemedial(response.data);
      setShowPostExamRemedial(true);
    } catch (error) {
      console.error('Error loading post-exam remedial:', error);
    }
  };

  // Load study plan on mount
  useEffect(() => {
    loadStudyPlan();
  }, []);

  // Test functions - Start test with pre-exam analysis
  const handleStartTest = (test) => {
    loadPreExamAnalysis(test);
  };

  const startTestDirectly = async (test) => {
    setShowPreExamAnalysis(false);
    setPreExamAnalysis(null);
    try {
      const response = await axios.post(`${API}/student/tests/${test.id}/start`);
      const attemptData = { ...response.data, id: response.data.attempt_id };
      setCurrentAttempt(attemptData);
      setCurrentTest(test);
      setTimeRemaining(test.duration_minutes * 60);
      setQuestionNumber(1);
      setSavedAnswers({});
      setAllQuestions([]);
      setShowReview(false);
      await loadQuestion(test.id, 1, response.data.attempt_id);
      toast.success('Test started! Good luck!');
    } catch (error) {
      console.error('Error starting test:', error);
      toast.error(error.response?.data?.detail || 'Failed to start test');
    }
  };

  // Original start test (now called by pre-exam modal)
  const startTest = async (test) => {
    try {
      const response = await axios.post(`${API}/student/tests/${test.id}/start`);
      // Backend returns attempt_id, not id
      const attemptData = { ...response.data, id: response.data.attempt_id };
      setCurrentAttempt(attemptData);
      setCurrentTest(test);
      setTimeRemaining(test.duration_minutes * 60);
      setQuestionNumber(1);
      setSavedAnswers({}); // Reset saved answers
      setAllQuestions([]); // Reset all questions
      setShowReview(false);
      setShowPreExamAnalysis(false);
      setPreExamAnalysis(null);
      await loadQuestion(test.id, 1, response.data.attempt_id);
      toast.success('Test started! Good luck!');
    } catch (error) {
      console.error('Error starting test:', error);
      toast.error(error.response?.data?.detail || 'Failed to start test');
    }
  };

  const loadQuestion = async (testId, qNum, attemptId = null) => {
    try {
      const aId = attemptId || currentAttempt?.id || currentAttempt?.attempt_id;
      const response = await axios.get(`${API}/student/tests/${testId}/question/${qNum}?attempt_id=${aId}`);
      setCurrentQuestion(response.data);
      // Set user answer if already saved
      const savedAnswer = savedAnswers[qNum];
      setUserAnswer(savedAnswer || '');
      
      // Add to allQuestions if not already there
      setAllQuestions(prev => {
        const exists = prev.find(q => q.question_number === qNum);
        if (!exists) {
          return [...prev, { ...response.data, question_number: qNum }];
        }
        return prev;
      });
    } catch (error) {
      if (error.response?.status === 404) {
        // No more questions - go to review
        setShowReview(true);
      } else {
        toast.error('Failed to load question');
      }
    }
  };

  const handleAnswerSubmit = async (goNext = true) => {
    if (!userAnswer) {
      toast.error('Please select an answer');
      return;
    }

    try {
      const attemptId = currentAttempt?.id || currentAttempt?.attempt_id;
      await axios.post(`${API}/student/tests/${currentTest.id}/answer?attempt_id=${attemptId}`, {
        question_id: currentQuestion.id,
        question_number: questionNumber,
        user_answer: userAnswer
      });
      
      // Save answer locally for review
      setSavedAnswers(prev => ({ ...prev, [questionNumber]: userAnswer }));
      
      if (goNext) {
        const nextQ = questionNumber + 1;
        if (nextQ <= currentTest.total_questions) {
          setQuestionNumber(nextQ);
          await loadQuestion(currentTest.id, nextQ);
        } else {
          // All questions answered, show review
          setShowReview(true);
        }
      }
    } catch (error) {
      toast.error('Failed to submit answer');
    }
  };

  const goToQuestion = async (qNum) => {
    // Save current answer first if exists
    if (userAnswer && !savedAnswers[questionNumber]) {
      setSavedAnswers(prev => ({ ...prev, [questionNumber]: userAnswer }));
    }
    setQuestionNumber(qNum);
    setShowReview(false);
    await loadQuestion(currentTest.id, qNum);
  };

  const handlePreviousQuestion = () => {
    if (questionNumber > 1) {
      // Save current answer
      if (userAnswer) {
        setSavedAnswers(prev => ({ ...prev, [questionNumber]: userAnswer }));
      }
      goToQuestion(questionNumber - 1);
    }
  };

  const handleNextQuestion = async () => {
    if (userAnswer) {
      await handleAnswerSubmit(true);
    } else if (questionNumber < currentTest.total_questions) {
      setQuestionNumber(questionNumber + 1);
      await loadQuestion(currentTest.id, questionNumber + 1);
    }
  };

  const handleSubmitTest = async () => {
    try {
      const attemptId = currentAttempt?.id || currentAttempt?.attempt_id;
      await axios.post(`${API}/student/tests/${currentTest.id}/submit?attempt_id=${attemptId}`);
      
      toast.success(`Test completed! Your result is pending teacher approval.`);
      
      // Store attempt ID for remedial content
      setLastCompletedAttemptId(attemptId);
      
      // Load post-exam remedial content
      loadPostExamRemedial(attemptId);
      
      setCurrentTest(null);
      setCurrentAttempt(null);
      setCurrentQuestion(null);
      setSavedAnswers({});
      setAllQuestions([]);
      setShowReview(false);
      loadDashboardData();
      loadStudyPlan(); // Refresh study plan
    } catch (error) {
      toast.error('Failed to submit test');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Filter tests
  const filteredTests = availableTests.filter(test => {
    if (filters.subject && test.subject !== filters.subject) return false;
    if (filters.term && test.category !== filters.term) return false;
    return true;
  });

  // If taking a test, show test interface
  if (currentTest && currentQuestion && !showReview) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        {/* Test Header */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white p-4 shadow-lg">
          <div className="max-w-6xl mx-auto flex justify-between items-center">
            <div>
              <h1 className="text-xl font-bold">{currentTest.title}</h1>
              <p className="text-teal-100 text-sm">{currentTest.subject} • {currentTest.category}</p>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2 bg-teal-500/30 px-4 py-2 rounded-lg">
                <Timer size={20} />
                <span className="font-mono text-lg font-bold">{formatTime(timeRemaining)}</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="text-white border-white/50 hover:bg-white/20"
                onClick={() => setShowReview(true)}
              >
                Review All
              </Button>
            </div>
          </div>
        </div>

        {/* Question Navigation */}
        <div className="bg-white border-b shadow-sm py-3">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              <span className="text-sm text-gray-500 mr-2">Questions:</span>
              {Array.from({ length: currentTest.total_questions }, (_, i) => i + 1).map(qNum => (
                <button
                  key={qNum}
                  onClick={() => goToQuestion(qNum)}
                  className={`w-10 h-10 rounded-full text-sm font-medium transition-all flex-shrink-0 ${
                    qNum === questionNumber
                      ? 'bg-teal-600 text-white'
                      : savedAnswers[qNum]
                        ? 'bg-green-100 text-green-700 border-2 border-green-400'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {qNum}
                </button>
              ))}
            </div>
            <div className="flex gap-4 text-xs text-gray-500 mt-2">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-green-400"></span> Answered ({Object.keys(savedAnswers).length})
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-gray-300"></span> Not answered ({currentTest.total_questions - Object.keys(savedAnswers).length})
              </span>
            </div>
          </div>
        </div>

        {/* Question Content */}
        <div className="max-w-4xl mx-auto p-6">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <span className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-medium">
                  Question {questionNumber} of {currentTest.total_questions}
                </span>
                <Badge variant="outline" className="text-gray-500">
                  {currentQuestion.question_type === 'mcq' ? 'Multiple Choice' : 
                   currentQuestion.question_type === 'true_false' ? 'True/False' : 'Short Answer'}
                </Badge>
              </div>
              <h2 className="text-xl font-semibold text-gray-800 leading-relaxed">
                {currentQuestion.question_text}
              </h2>
            </div>

            <div className="space-y-3 mb-8">
              {/* MCQ / True-False Options */}
              {currentQuestion.options && currentQuestion.options.length > 0 ? (
                currentQuestion.options.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => setUserAnswer(option)}
                    data-testid={`option-${index}`}
                    className={`w-full p-4 text-left rounded-xl border-2 transition-all ${
                      userAnswer === option
                        ? 'border-teal-500 bg-teal-50 text-teal-700 shadow-md'
                        : 'border-gray-200 hover:border-teal-300 hover:bg-gray-50'
                    }`}
                  >
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-600 font-medium mr-3">
                      {String.fromCharCode(65 + index)}
                    </span>
                    {option}
                  </button>
                ))
              ) : (
                /* Short Answer / Fill in the Blank Input */
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-600">Your Answer:</label>
                  <Input
                    value={userAnswer}
                    onChange={(e) => setUserAnswer(e.target.value)}
                    placeholder="Type your answer here..."
                    className="w-full p-4 text-lg border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:ring-2 focus:ring-teal-200"
                    data-testid="short-answer-input"
                  />
                </div>
              )}
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-between items-center pt-4 border-t">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handlePreviousQuestion}
                  disabled={questionNumber === 1}
                  className="flex items-center gap-2"
                >
                  <ChevronLeft size={18} /> Previous
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    if (window.confirm('Are you sure you want to end the test early?')) {
                      setShowReview(true);
                    }
                  }}
                  className="text-orange-600 border-orange-200 hover:bg-orange-50"
                >
                  Review & Submit
                </Button>
              </div>
              <div className="flex gap-2">
                {questionNumber < currentTest.total_questions ? (
                  <Button
                    onClick={handleNextQuestion}
                    disabled={!userAnswer}
                    className="bg-teal-600 hover:bg-teal-700 flex items-center gap-2"
                  >
                    Save & Next <ChevronRight size={18} />
                  </Button>
                ) : (
                  <Button
                    onClick={() => {
                      if (userAnswer) {
                        handleAnswerSubmit(false).then(() => setShowReview(true));
                      } else {
                        setShowReview(true);
                      }
                    }}
                    className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
                  >
                    <CheckCircle size={18} /> Review & Submit
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Review Page - Before Final Submit
  if (currentTest && showReview) {
    const answeredCount = Object.keys(savedAnswers).length;
    const unansweredCount = currentTest.total_questions - answeredCount;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-4 shadow-lg">
          <div className="max-w-4xl mx-auto flex justify-between items-center">
            <div>
              <h1 className="text-xl font-bold">Review Your Answers</h1>
              <p className="text-orange-100 text-sm">{currentTest.title}</p>
            </div>
            <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-lg">
              <Timer size={20} />
              <span className="font-mono text-lg font-bold">{formatTime(timeRemaining)}</span>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto p-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-xl p-4 shadow-sm text-center">
              <p className="text-3xl font-bold text-gray-800">{currentTest.total_questions}</p>
              <p className="text-sm text-gray-500">Total Questions</p>
            </div>
            <div className="bg-green-50 rounded-xl p-4 shadow-sm text-center border-2 border-green-200">
              <p className="text-3xl font-bold text-green-600">{answeredCount}</p>
              <p className="text-sm text-green-600">Answered</p>
            </div>
            <div className={`rounded-xl p-4 shadow-sm text-center border-2 ${unansweredCount > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
              <p className={`text-3xl font-bold ${unansweredCount > 0 ? 'text-red-600' : 'text-gray-400'}`}>{unansweredCount}</p>
              <p className={`text-sm ${unansweredCount > 0 ? 'text-red-600' : 'text-gray-400'}`}>Unanswered</p>
            </div>
          </div>

          {/* Questions List */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
            <div className="bg-gray-100 px-6 py-3 border-b">
              <h3 className="font-semibold text-gray-700">Questions Overview</h3>
            </div>
            <div className="divide-y">
              {Array.from({ length: currentTest.total_questions }, (_, i) => i + 1).map(qNum => {
                const answered = savedAnswers[qNum];
                return (
                  <div 
                    key={qNum} 
                    className={`px-6 py-4 flex items-center justify-between hover:bg-gray-50 cursor-pointer ${!answered ? 'bg-red-50' : ''}`}
                    onClick={() => goToQuestion(qNum)}
                  >
                    <div className="flex items-center gap-4">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        answered ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                      }`}>
                        {qNum}
                      </span>
                      <span className="text-gray-700">Question {qNum}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {answered ? (
                        <>
                          <span className="text-sm text-gray-500 max-w-xs truncate">Answer: {answered}</span>
                          <CheckCircle size={18} className="text-green-500" />
                        </>
                      ) : (
                        <>
                          <span className="text-sm text-red-500">Not answered</span>
                          <AlertCircle size={18} className="text-red-500" />
                        </>
                      )}
                      <ChevronRight size={18} className="text-gray-400" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Warning if unanswered */}
          {unansweredCount > 0 && (
            <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-4 mb-6 flex items-start gap-3">
              <AlertTriangle className="text-yellow-600 flex-shrink-0 mt-1" size={24} />
              <div>
                <p className="font-semibold text-yellow-800">You have {unansweredCount} unanswered question(s)</p>
                <p className="text-sm text-yellow-700">Click on any question above to go back and answer it.</p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={() => goToQuestion(1)}
              className="flex items-center gap-2"
            >
              <ChevronLeft size={18} /> Back to Questions
            </Button>
            <Button
              onClick={() => {
                if (unansweredCount > 0) {
                  if (window.confirm(`You have ${unansweredCount} unanswered questions. Are you sure you want to submit?`)) {
                    handleSubmitTest();
                  }
                } else {
                  if (window.confirm('Are you sure you want to submit the test?')) {
                    handleSubmitTest();
                  }
                }
              }}
              className="bg-green-600 hover:bg-green-700 flex items-center gap-2 px-8"
            >
              <CheckCircle size={18} /> Submit Test
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // If audio learning
  if (activeSection === 'audio') {
    return <AudioLearning onBack={() => setActiveSection('dashboard')} />;
  }

  // If skill assessment
  if (activeSection === 'skills') {
    return <SkillAssessment onBack={() => setActiveSection('dashboard')} studentClass={user?.class_level || "6"} />;
  }

  // If practice mock (now "Mocks")
  if (activeSection === 'mocks') {
    return <PracticeMock onBack={() => setActiveSection('dashboard')} studentClass={user?.class_level || "6"} />;
  }

  // Mock Exams (teacher-published unit/quarterly/half-yearly/annual)
  if (activeSection === 'mock-exams') {
    return <StudentMockExams onBack={() => setActiveSection('dashboard')} />;
  }

  // Main Dashboard
  return (
    <div className="flex min-h-screen bg-gray-100" data-testid="student-dashboard">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}
      {/* Sidebar */}
      <aside className={`fixed md:static inset-y-0 left-0 z-50 w-64 bg-gradient-to-b from-teal-800 to-teal-900 text-white flex flex-col shadow-xl transform transition-transform duration-200 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
        {/* Logo/Brand */}
        <div className="p-4 border-b border-teal-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
              <BookOpen className="text-teal-600" size={24} />
            </div>
            <div>
              <h1 className="font-bold text-lg">MyClassAI</h1>
              <p className="text-teal-300 text-xs">Class {user?.class_level || '6'}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-2 overflow-y-auto">
          <MenuItem
            icon={Home}
            label="Dashboard"
            active={activeSection === 'dashboard'}
            onClick={() => setActiveSection('dashboard')}
          />
          
          <MenuItem
            icon={Headphones}
            label="Audio Learning"
            active={activeSection === 'audio'}
            onClick={() => setActiveSection('audio')}
          />

          <MenuItem
            icon={Bell}
            label="Announcements"
            active={activeSection === 'announcements'}
            onClick={() => setActiveSection('announcements')}
          />

          <MenuItem
            icon={Sparkles}
            label="Ask AI Tutor"
            active={activeSection === 'doubt-solver'}
            onClick={() => setActiveSection('doubt-solver')}
          />

          <MenuItem
            icon={FileText}
            label="Class Exams"
            active={activeSection === 'mock-exams'}
            onClick={() => setActiveSection('mock-exams')}
          />

          <MenuItem
            icon={Brain}
            label="Topic Tests"
            active={activeSection === 'skills'}
            onClick={() => setActiveSection('skills')}
          />

          <MenuItem
            icon={BarChart3}
            label="Progress"
            active={activeSection === 'progress' || expandedMenus.progress}
            hasSubmenu
            isOpen={expandedMenus.progress}
            onClick={() => toggleMenu('progress')}
          >
            <SubMenuItem 
              label="My Test Results" 
              active={activeSubSection === 'test-scores'}
              onClick={() => { setActiveSection('progress'); setActiveSubSection('test-scores'); }}
            />
            <SubMenuItem 
              label="Weekly Activity" 
              active={activeSubSection === 'weekly-avg'}
              onClick={() => { setActiveSection('progress'); setActiveSubSection('weekly-avg'); }}
            />
            <SubMenuItem 
              label="Subject Averages" 
              active={activeSubSection === 'analysis'}
              onClick={() => { setActiveSection('progress'); setActiveSubSection('analysis'); }}
            />
            <SubMenuItem 
              label="Improvement" 
              active={activeSubSection === 'improvement'}
              onClick={() => { setActiveSection('progress'); setActiveSubSection('improvement'); }}
            />
          </MenuItem>

          <MenuItem
            icon={Award}
            label="Awards"
            active={activeSection === 'awards'}
            onClick={() => setActiveSection('awards')}
          />

          <MenuItem
            icon={Wallet}
            label="My Fees"
            active={activeSection === 'fees'}
            onClick={() => setActiveSection('fees')}
          />

          <MenuItem
            icon={Calendar}
            label="Calendar"
            active={activeSection === 'calendar'}
            onClick={() => setActiveSection('calendar')}
          />

          <MenuItem
            icon={HelpCircle}
            label="Support"
            active={activeSection === 'support'}
            onClick={() => setActiveSection('support')}
          />
        </nav>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-teal-700">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold">{user?.name?.charAt(0) || 'S'}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{user?.name || 'Student'}</p>
              <p className="text-teal-300 text-xs">{user?.roll_number || ''}</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-teal-200 hover:text-white hover:bg-teal-700"
            onClick={handleLogout}
          >
            <LogOut size={18} className="mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Top Bar */}
        <header className="bg-white shadow-sm px-4 md:px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100"
              data-testid="student-mobile-menu-btn"
            >
              <Menu size={24} />
            </button>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-800">
              {activeSection === 'mocks' && 'Topic Tests'}
              {activeSection === 'mock-exams' && 'Class Exams'}
              {activeSection === 'dashboard' && 'Dashboard'}
              {activeSection === 'progress' && 'My Progress'}
              {activeSection === 'homework' && 'Homework'}
              {activeSection === 'audio' && 'Audio Learning'}
              {activeSection === 'awards' && 'Awards'}
              {activeSection === 'fees' && 'My Fees'}
              {activeSection === 'calendar' && 'Calendar'}
              {activeSection === 'announcements' && 'Announcements'}
              {activeSection === 'doubt-solver' && 'Ask AI Tutor'}
              {activeSection === 'support' && 'Support'}
              {activeSection === 'mental-health' && 'Mental Health Check-in'}
              {activeSection === 'online-classes' && 'Online Classes'}
            </h2>
            <p className="text-gray-500 text-sm">Welcome back, {user?.name || 'Student'}!</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm text-gray-500">Class {user?.class_level || '6'}</p>
              <p className="text-xs text-gray-400">{format(new Date(), 'dd MMM yyyy')}</p>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <div className="p-3 md:p-6">
          {/* Dashboard View */}
          {activeSection === 'dashboard' && (
            <div className="space-y-6">
              {/* Daily Motivation banner — only shows once per day */}
              <DailyMotivation />
              {/* Holiday Calendar */}
              <HolidayCalendar role="student" />

              {/* Practice assigned by teacher */}
              {myAssignments.total > 0 && (
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-xl p-5" data-testid="assigned-practices-card">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 text-white flex items-center justify-center shrink-0">
                        <Target size={22} />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-800">
                          {myAssignments.total} practice{myAssignments.total === 1 ? '' : 's'} assigned by your teacher
                        </h3>
                        <p className="text-xs text-gray-600">Tap a card below to start. Each one is targeted to a chapter you can improve in.</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2">
                    {myAssignments.assignments.slice(0, 6).map(a => (
                      <div
                        key={a.id}
                        onClick={() => { setActiveSection('skill-assessment'); }}
                        className="bg-white rounded-lg p-3 border border-indigo-100 hover:border-indigo-400 cursor-pointer flex items-center justify-between gap-3 transition-all"
                        data-testid={`assignment-${a.id}`}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-sm text-gray-800 truncate">{a.title}</p>
                          <p className="text-xs text-gray-500 mt-0.5 truncate">
                            {a.subject ? `${a.subject} · ` : ''}{a.chapter_name || 'Practice'} · {a.teacher_name}
                          </p>
                        </div>
                        <span className="text-xs font-semibold text-indigo-600 shrink-0">Start →</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl p-5 shadow-sm border-l-4 border-teal-500 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveSection('skill-assessment')}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-500 text-sm">Topic Tests</p>
                      <p className="text-lg font-bold text-teal-600">Start Practicing</p>
                    </div>
                    <Target className="text-teal-500" size={40} />
                  </div>
                </div>
                <div className="bg-white rounded-xl p-5 shadow-sm border-l-4 border-blue-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-500 text-sm">Completed Tests</p>
                      <p className="text-3xl font-bold text-gray-800">{approvedResults.length}</p>
                    </div>
                    <CheckCircle className="text-blue-500" size={40} />
                  </div>
                </div>
                <div className="bg-white rounded-xl p-5 shadow-sm border-l-4 border-green-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-500 text-sm">Average Score</p>
                      <p className="text-3xl font-bold text-gray-800">
                        {approvedResults.length > 0 
                          ? Math.round(approvedResults.reduce((a, r) => a + r.score, 0) / approvedResults.length)
                          : 0}%
                      </p>
                    </div>
                    <Award className="text-green-500" size={40} />
                  </div>
                </div>
                <div className="bg-white rounded-xl p-5 shadow-sm border-l-4 border-purple-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-500 text-sm">Attendance</p>
                      <p className="text-3xl font-bold text-gray-800" data-testid="attendance-percentage">{attendanceData.percentage}%</p>
                      <p className="text-xs text-gray-400 mt-0.5">{attendanceData.present}/{attendanceData.total_days} days present</p>
                    </div>
                    <Calendar className={`size-10 ${
                      attendanceData.percentage >= 90 ? 'text-emerald-500' :
                      attendanceData.percentage >= 75 ? 'text-amber-500' :
                      'text-red-500'
                    }`} />
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <button
                    onClick={() => setActiveSection('doubt-solver')}
                    className="flex flex-col items-center p-4 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white hover:shadow-lg transition-all relative overflow-hidden"
                    data-testid="quick-action-ai-tutor"
                  >
                    <Sparkles className="mb-2" size={32} />
                    <span className="text-sm font-medium">Ask AI Tutor</span>
                    <span className="absolute top-1 right-1 text-[9px] bg-amber-400 text-amber-900 px-1.5 py-0.5 rounded-full font-bold">NEW</span>
                  </button>
                  <button 
                    onClick={() => setActiveSection('skill-assessment')}
                    className="flex flex-col items-center p-4 rounded-xl bg-teal-50 hover:bg-teal-100 transition-colors"
                  >
                    <Target className="text-teal-600 mb-2" size={32} />
                    <span className="text-sm font-medium">Take a Test</span>
                  </button>
                  <button 
                    onClick={() => setActiveSection('audio')}
                    className="flex flex-col items-center p-4 rounded-xl bg-blue-50 hover:bg-blue-100 transition-colors"
                  >
                    <Volume2 className="text-blue-600 mb-2" size={32} />
                    <span className="text-sm font-medium">Audio Learning</span>
                  </button>
                  <button 
                    onClick={() => { setActiveSection('progress'); setActiveSubSection('test-scores'); toggleMenu('progress'); }}
                    className="flex flex-col items-center p-4 rounded-xl bg-green-50 hover:bg-green-100 transition-colors"
                  >
                    <BarChart3 className="text-green-600 mb-2" size={32} />
                    <span className="text-sm font-medium">View Progress</span>
                  </button>
                </div>
              </div>

              {/* Personalized Study Plan Card */}
              {studyPlan?.has_data && (
                <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-lg overflow-hidden" data-testid="study-plan-card">
                  <div className="p-6 text-white">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-white/20 rounded-lg">
                          <Route size={24} />
                        </div>
                        <div>
                          <h3 className="font-bold text-lg">Your Study Plan</h3>
                          <p className="text-white/70 text-sm">Based on {studyPlan.total_exams_analyzed} exams</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex gap-1">
                          <span className="bg-red-400 text-white text-xs px-2 py-1 rounded">
                            {studyPlan.summary.weak_count} weak
                          </span>
                          <span className="bg-green-400 text-white text-xs px-2 py-1 rounded">
                            {studyPlan.summary.strong_count} strong
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Priority Topics */}
                    {studyPlan.priority_topics?.length > 0 && (
                      <div className="mb-4">
                        <p className="text-white/80 text-sm mb-2">🔴 Focus on:</p>
                        <div className="flex flex-wrap gap-2">
                          {studyPlan.priority_topics.map((topic, i) => (
                            <Badge key={i} className="bg-red-500/30 text-white border border-red-300/50">
                              {topic}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* This Week's Plan */}
                    {studyPlan.study_plan?.[0] && (
                      <div className="bg-white/10 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold">{studyPlan.study_plan[0].days}: {studyPlan.study_plan[0].focus}</p>
                            <p className="text-white/70 text-sm">{studyPlan.study_plan[0].goal}</p>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => setActiveSection('skills')}
                            className="bg-white text-purple-600 hover:bg-white/90"
                          >
                            Start
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* No Study Plan Yet */}
              {studyPlan && !studyPlan.has_data && (
                <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl p-6" data-testid="no-study-plan">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-gray-300 rounded-xl">
                      <Route size={32} className="text-gray-500" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-700">Get Your Personalized Study Plan</h3>
                      <p className="text-gray-500 text-sm">Complete mock exams to receive AI-powered study recommendations</p>
                    </div>
                    <Button
                      onClick={() => setActiveSection('skill-assessment')}
                      className="ml-auto bg-teal-600 hover:bg-teal-700"
                    >
                      Take a Mock
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Progress View - ENHANCED */}
          {activeSection === 'progress' && (
            <div className="space-y-6">
              {/* Enhanced Sub-section tabs */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="px-5 py-3 border-b bg-gray-50">
                  <p className="text-xs text-gray-500">
                    Showing your results across <strong>Topic Tests</strong>, and <strong>Class Exams</strong> (Unit Tests, Quarterly, Half-Yearly, Finals).
                  </p>
                </div>
                <div className="flex border-b">
                  {[
                    { id: 'test-scores', label: 'My Test Results', icon: FileText, color: 'teal' },
                    { id: 'weekly-avg', label: 'Weekly Activity', icon: TrendingUp, color: 'blue' },
                    { id: 'analysis', label: 'Subject Averages', icon: Target, color: 'indigo' },
                    { id: 'improvement', label: 'Improvement', icon: TrendingUp, color: 'emerald' }
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveSubSection(tab.id)}
                      data-testid={`progress-subtab-${tab.id}`}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-4 text-sm font-medium transition-all ${
                        (activeSubSection === tab.id || (!activeSubSection && tab.id === 'test-scores'))
                          ? `bg-${tab.color}-600 text-white`
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                      style={{
                        backgroundColor: (activeSubSection === tab.id || (!activeSubSection && tab.id === 'test-scores'))
                          ? tab.color === 'teal' ? '#0d9488' 
                          : tab.color === 'blue' ? '#2563eb'
                          : tab.color === 'emerald' ? '#059669'
                          : '#4f46e5'
                          : undefined
                      }}
                    >
                      <tab.icon size={18} />
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* ========== TEST SCORES VIEW ========== */}
              {(activeSubSection === 'test-scores' || !activeSubSection) && (
                <div className="space-y-6">
                  {/* Quick Stats Row */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl p-4 text-white">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-teal-100 text-sm">Total Tests</p>
                          <p className="text-3xl font-bold">{approvedResults.length + pendingResults.length}</p>
                        </div>
                        <FileText className="opacity-50" size={40} />
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-green-100 text-sm">Approved</p>
                          <p className="text-3xl font-bold">{approvedResults.length}</p>
                        </div>
                        <CheckCircle className="opacity-50" size={40} />
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-4 text-white">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-orange-100 text-sm">Pending</p>
                          <p className="text-3xl font-bold">{pendingResults.length}</p>
                        </div>
                        <Clock className="opacity-50" size={40} />
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-blue-100 text-sm">Avg Score</p>
                          <p className="text-3xl font-bold">
                            {approvedResults.length > 0 
                              ? Math.round(approvedResults.reduce((s,r) => s + (r.score||0), 0) / approvedResults.length)
                              : 0}%
                          </p>
                        </div>
                        <BarChart3 className="opacity-50" size={40} />
                      </div>
                    </div>
                  </div>

                  {/* Pending Results */}
                  {pendingResults.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm overflow-hidden border-l-4 border-orange-500">
                      <div className="bg-gradient-to-r from-orange-50 to-white px-6 py-4 flex justify-between items-center">
                        <h3 className="font-semibold text-orange-800 flex items-center gap-2">
                          <Clock size={20} className="text-orange-500" />
                          Awaiting Teacher Approval ({pendingResults.length})
                        </h3>
                      </div>
                      <div className="divide-y">
                        {pendingResults.map((result, index) => (
                          <div key={result.attempt_id} className="px-6 py-4 flex items-center justify-between hover:bg-orange-50/50 transition-colors">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                                <FileText size={20} className="text-orange-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-800">{result.test_title || 'Test'}</p>
                                <p className="text-sm text-gray-500">{result.subject} • {result.category}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className={`text-lg font-bold ${
                                  result.score >= 75 ? 'text-green-600' :
                                  result.score >= 50 ? 'text-yellow-600' : 'text-red-600'
                                }`}>{result.score}%</p>
                                <p className="text-xs text-gray-400">
                                  {result.completed_at ? format(new Date(result.completed_at), 'dd MMM') : '-'}
                                </p>
                              </div>
                              <Badge className="bg-orange-100 text-orange-700 border-orange-200">Pending</Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Approved Results */}
                  <div className="bg-white rounded-xl shadow-sm overflow-hidden border-l-4 border-green-500">
                    <div className="bg-gradient-to-r from-green-50 to-white px-6 py-4 flex justify-between items-center">
                      <h3 className="font-semibold text-green-800 flex items-center gap-2">
                        <CheckCircle size={20} className="text-green-500" />
                        Approved Results ({approvedResults.length})
                      </h3>
                    </div>
                    {approvedResults.length > 0 ? (
                      <div className="divide-y">
                        {approvedResults.map((result, index) => (
                          <div key={result.attempt_id || index} className="px-6 py-4 flex items-center justify-between hover:bg-green-50/50 transition-colors">
                            <div className="flex items-center gap-4">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                result.score >= 75 ? 'bg-green-100' :
                                result.score >= 50 ? 'bg-yellow-100' : 'bg-red-100'
                              }`}>
                                {result.score >= 75 ? <Star size={20} className="text-green-600" /> :
                                 result.score >= 50 ? <Zap size={20} className="text-yellow-600" /> :
                                 <AlertTriangle size={20} className="text-red-600" />}
                              </div>
                              <div>
                                <p className="font-medium text-gray-800">{result.test_title || 'Test'}</p>
                                <p className="text-sm text-gray-500">{result.subject} • {result.category}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className={`text-lg font-bold ${
                                  result.score >= 75 ? 'text-green-600' :
                                  result.score >= 50 ? 'text-yellow-600' : 'text-red-600'
                                }`}>{result.score}%</p>
                                <p className="text-xs text-gray-400">
                                  {result.completed_at ? format(new Date(result.completed_at), 'dd MMM yyyy') : '-'}
                                </p>
                              </div>
                              <Badge className="bg-green-100 text-green-700 border-green-200">Approved</Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="px-6 py-12 text-center">
                        <GraduationCap className="mx-auto text-gray-300 mb-4" size={48} />
                        <p className="text-gray-500">No approved results yet</p>
                        <p className="text-sm text-gray-400 mt-1">Complete tests and wait for teacher approval</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ========== WEEKLY AVERAGE VIEW ========== */}
              {activeSubSection === 'weekly-avg' && (
                <div className="space-y-6">
                  {/* Performance Overview Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-6 text-white">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-blue-200 text-sm font-medium">Overall Average</span>
                        <div className="p-2 bg-blue-500/30 rounded-lg">
                          <BarChart3 size={24} />
                        </div>
                      </div>
                      <p className="text-4xl font-bold">
                        {approvedResults.length > 0 
                          ? Math.round(approvedResults.reduce((s,r) => s + (r.score||0), 0) / approvedResults.length)
                          : 0}%
                      </p>
                      <p className="text-blue-200 text-sm mt-2">
                        Based on {approvedResults.length} test{approvedResults.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                    
                    <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-xl p-6 text-white">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-green-200 text-sm font-medium">Best Performance</span>
                        <div className="p-2 bg-green-500/30 rounded-lg">
                          <TrendingUp size={24} />
                        </div>
                      </div>
                      <p className="text-4xl font-bold">
                        {approvedResults.length > 0 ? Math.max(...approvedResults.map(r => r.score || 0)) : 0}%
                      </p>
                      <p className="text-green-200 text-sm mt-2">Highest score achieved</p>
                    </div>
                    
                    <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-xl p-6 text-white">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-purple-200 text-sm font-medium">This Week</span>
                        <div className="p-2 bg-purple-500/30 rounded-lg">
                          <Calendar size={24} />
                        </div>
                      </div>
                      {(() => {
                        const weekStart = startOfWeek(new Date());
                        const weekEnd = endOfWeek(new Date());
                        const thisWeekResults = approvedResults.filter(r => 
                          r.completed_at && isWithinInterval(new Date(r.completed_at), { start: weekStart, end: weekEnd })
                        );
                        return (
                          <>
                            <p className="text-4xl font-bold">{thisWeekResults.length}</p>
                            <p className="text-purple-200 text-sm mt-2">Tests completed this week</p>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Score Distribution Chart */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <BarChart3 size={20} className="text-blue-600" />
                      Score Distribution
                    </h3>
                    <div className="space-y-4">
                      {[
                        { label: 'Excellent (75-100%)', color: 'green', count: approvedResults.filter(r => r.score >= 75).length },
                        { label: 'Good (50-74%)', color: 'yellow', count: approvedResults.filter(r => r.score >= 50 && r.score < 75).length },
                        { label: 'Needs Improvement (<50%)', color: 'red', count: approvedResults.filter(r => r.score < 50).length }
                      ].map(item => (
                        <div key={item.label}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-600">{item.label}</span>
                            <span className="font-medium">{item.count} test{item.count !== 1 ? 's' : ''}</span>
                          </div>
                          <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                            <div 
                              className={`h-full transition-all duration-500 ${
                                item.color === 'green' ? 'bg-green-500' :
                                item.color === 'yellow' ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ 
                                width: approvedResults.length > 0 
                                  ? `${(item.count / approvedResults.length) * 100}%` 
                                  : '0%' 
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Recent Performance Trend */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <TrendingUp size={20} className="text-green-600" />
                      Recent Performance (Last 5 Tests)
                    </h3>
                    {approvedResults.length > 0 ? (
                      <div className="space-y-3">
                        {approvedResults
                          .sort((a, b) => new Date(b.completed_at || 0) - new Date(a.completed_at || 0))
                          .slice(0, 5)
                          .map((result, index, arr) => {
                            const prevScore = arr[index + 1]?.score;
                            const trend = prevScore ? result.score - prevScore : 0;
                            return (
                              <div key={index} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${
                                  result.score >= 75 ? 'bg-green-100 text-green-700' :
                                  result.score >= 50 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                                }`}>
                                  {result.score}%
                                </div>
                                <div className="flex-1">
                                  <p className="font-medium text-gray-800">{result.test_title || 'Test'}</p>
                                  <p className="text-sm text-gray-500">
                                    {result.subject} • {result.completed_at ? format(new Date(result.completed_at), 'dd MMM yyyy') : '-'}
                                  </p>
                                </div>
                                {trend !== 0 && (
                                  <div className={`flex items-center gap-1 text-sm font-medium ${
                                    trend > 0 ? 'text-green-600' : 'text-red-600'
                                  }`}>
                                    {trend > 0 ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
                                    {Math.abs(trend)}%
                                  </div>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <TrendingUp className="mx-auto text-gray-300 mb-3" size={48} />
                        <p className="text-gray-500">No performance data yet</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ========== FINISHED TESTS VIEW ========== */}
              {activeSubSection === 'finished' && (
                <div className="space-y-6">
                  {/* Summary Banner */}
                  <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 rounded-xl p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-2xl font-bold mb-1">
                          {approvedResults.length + pendingResults.length} Tests Completed
                        </h3>
                        <p className="text-purple-200">
                          {approvedResults.length} approved • {pendingResults.length} pending approval
                        </p>
                      </div>
                      <div className="hidden md:flex items-center gap-8">
                        <div className="text-center">
                          <p className="text-3xl font-bold">
                            {approvedResults.length > 0 
                              ? Math.round(approvedResults.reduce((s,r) => s + (r.score||0), 0) / approvedResults.length)
                              : 0}%
                          </p>
                          <p className="text-purple-200 text-sm">Avg Score</p>
                        </div>
                        <div className="text-center">
                          <p className="text-3xl font-bold">
                            {approvedResults.length > 0 ? Math.max(...approvedResults.map(r => r.score || 0)) : 0}%
                          </p>
                          <p className="text-purple-200 text-sm">Best Score</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* All Tests Grid */}
                  <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b flex justify-between items-center">
                      <h3 className="font-semibold text-gray-800">All Completed Tests</h3>
                      <div className="flex gap-2">
                        <Badge variant="outline" className="bg-green-50 text-green-700">{approvedResults.length} Approved</Badge>
                        <Badge variant="outline" className="bg-orange-50 text-orange-700">{pendingResults.length} Pending</Badge>
                      </div>
                    </div>
                    
                    {(approvedResults.length + pendingResults.length) > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
                        {[...approvedResults.map(r => ({...r, status: 'approved'})), 
                          ...pendingResults.map(r => ({...r, status: 'pending'}))]
                          .sort((a, b) => new Date(b.completed_at || 0) - new Date(a.completed_at || 0))
                          .map((result, index) => (
                            <div 
                              key={result.attempt_id || index} 
                              className={`rounded-xl p-4 border-2 transition-all hover:shadow-md ${
                                result.status === 'approved' ? 'border-green-200 bg-green-50/50' : 'border-orange-200 bg-orange-50/50'
                              }`}
                            >
                              <div className="flex items-start justify-between mb-3">
                                <div>
                                  <h4 className="font-semibold text-gray-800">{result.test_title || 'Test'}</h4>
                                  <p className="text-sm text-gray-500">{result.subject}</p>
                                </div>
                                <Badge className={result.status === 'approved' ? 'bg-green-500' : 'bg-orange-500'}>
                                  {result.status === 'approved' ? 'Approved' : 'Pending'}
                                </Badge>
                              </div>
                              
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                  <div className={`text-2xl font-bold ${
                                    result.score >= 75 ? 'text-green-600' :
                                    result.score >= 50 ? 'text-yellow-600' : 'text-red-600'
                                  }`}>
                                    {result.score}%
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    <p>{result.category}</p>
                                    <p>{result.completed_at ? format(new Date(result.completed_at), 'dd MMM yyyy') : '-'}</p>
                                  </div>
                                </div>
                                
                                {/* Mini progress bar */}
                                <div className="w-24">
                                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                    <div 
                                      className={`h-full ${
                                        result.score >= 75 ? 'bg-green-500' :
                                        result.score >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                                      }`}
                                      style={{ width: `${result.score}%` }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <div className="px-6 py-12 text-center">
                        <BookMarked className="mx-auto text-gray-300 mb-4" size={48} />
                        <p className="text-gray-500">No tests completed yet</p>
                        <p className="text-sm text-gray-400 mt-1">Start taking tests from Topic Tests!</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ========== SUBJECT ANALYSIS VIEW ========== */}
              {activeSubSection === 'analysis' && (
                <div className="space-y-6">
                  {/* Subject Comparison Overview */}
                  {approvedResults.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm p-6">
                      <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <Target size={20} className="text-indigo-600" />
                        Subject Comparison
                      </h3>
                      <div className="grid grid-cols-3 gap-4">
                        {['Mathematics', 'Science', 'English'].map(subject => {
                          const subjectResults = approvedResults.filter(r => r.subject === subject);
                          const avgScore = subjectResults.length > 0 
                            ? Math.round(subjectResults.reduce((s,r) => s + (r.score||0), 0) / subjectResults.length)
                            : 0;
                          const color = subject === 'Mathematics' ? 'blue' : subject === 'Science' ? 'green' : 'purple';
                          
                          return (
                            <div key={subject} className="text-center">
                              <div className={`relative w-24 h-24 mx-auto mb-3`}>
                                <svg className="w-24 h-24 transform -rotate-90">
                                  <circle cx="48" cy="48" r="40" stroke="#e5e7eb" strokeWidth="8" fill="none" />
                                  <circle 
                                    cx="48" cy="48" r="40" 
                                    stroke={color === 'blue' ? '#3b82f6' : color === 'green' ? '#22c55e' : '#a855f7'}
                                    strokeWidth="8" 
                                    fill="none"
                                    strokeDasharray={`${avgScore * 2.51} 251`}
                                    strokeLinecap="round"
                                  />
                                </svg>
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <span className={`text-xl font-bold ${
                                    color === 'blue' ? 'text-blue-600' : color === 'green' ? 'text-green-600' : 'text-purple-600'
                                  }`}>{avgScore}%</span>
                                </div>
                              </div>
                              <p className="font-medium text-gray-800">{subject}</p>
                              <p className="text-sm text-gray-500">{subjectResults.length} test{subjectResults.length !== 1 ? 's' : ''}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Detailed Subject Cards */}
                  {['Mathematics', 'Science', 'English'].map(subject => {
                    const subjectResults = approvedResults.filter(r => r.subject === subject);
                    if (subjectResults.length === 0) return null;
                    
                    const avgScore = Math.round(subjectResults.reduce((s,r) => s + (r.score||0), 0) / subjectResults.length);
                    const highestScore = Math.max(...subjectResults.map(r => r.score || 0));
                    const lowestScore = Math.min(...subjectResults.map(r => r.score || 0));
                    const recentTests = subjectResults.sort((a,b) => new Date(b.completed_at||0) - new Date(a.completed_at||0)).slice(0, 3);
                    
                    const color = subject === 'Mathematics' ? 'blue' : subject === 'Science' ? 'green' : 'purple';
                    const gradientFrom = color === 'blue' ? 'from-blue-500' : color === 'green' ? 'from-green-500' : 'from-purple-500';
                    const gradientTo = color === 'blue' ? 'to-blue-600' : color === 'green' ? 'to-green-600' : 'to-purple-600';
                    
                    return (
                      <div key={subject} className="bg-white rounded-xl shadow-sm overflow-hidden">
                        <div className={`bg-gradient-to-r ${gradientFrom} ${gradientTo} px-6 py-4`}>
                          <div className="flex justify-between items-center text-white">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-white/20 rounded-lg">
                                <BookOpen size={24} />
                              </div>
                              <div>
                                <h3 className="text-xl font-bold">{subject}</h3>
                                <p className="text-white/80 text-sm">{subjectResults.length} tests completed</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-3xl font-bold">{avgScore}%</p>
                              <p className="text-white/80 text-sm">Average</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="p-6">
                          {/* Stats Row */}
                          <div className="grid grid-cols-4 gap-4 mb-6">
                            <div className="text-center p-3 bg-gray-50 rounded-lg">
                              <p className="text-2xl font-bold text-gray-800">{subjectResults.length}</p>
                              <p className="text-xs text-gray-500">Tests Taken</p>
                            </div>
                            <div className="text-center p-3 bg-green-50 rounded-lg">
                              <p className="text-2xl font-bold text-green-600">{highestScore}%</p>
                              <p className="text-xs text-green-600">Highest</p>
                            </div>
                            <div className="text-center p-3 bg-red-50 rounded-lg">
                              <p className="text-2xl font-bold text-red-600">{lowestScore}%</p>
                              <p className="text-xs text-red-600">Lowest</p>
                            </div>
                            <div className="text-center p-3 bg-blue-50 rounded-lg">
                              <p className="text-2xl font-bold text-blue-600">{highestScore - lowestScore}%</p>
                              <p className="text-xs text-blue-600">Range</p>
                            </div>
                          </div>
                          
                          {/* Performance Bar */}
                          <div className="mb-6">
                            <div className="flex justify-between text-sm mb-2">
                              <span className="text-gray-600">Performance Level</span>
                              <span className={`font-medium ${
                                avgScore >= 75 ? 'text-green-600' :
                                avgScore >= 50 ? 'text-yellow-600' : 'text-red-600'
                              }`}>
                                {avgScore >= 75 ? 'Excellent' : avgScore >= 50 ? 'Good' : 'Needs Improvement'}
                              </span>
                            </div>
                            <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
                              <div 
                                className={`h-full transition-all duration-500 ${
                                  avgScore >= 75 ? 'bg-gradient-to-r from-green-400 to-green-500' :
                                  avgScore >= 50 ? 'bg-gradient-to-r from-yellow-400 to-yellow-500' :
                                  'bg-gradient-to-r from-red-400 to-red-500'
                                }`}
                                style={{ width: `${avgScore}%` }}
                              />
                            </div>
                          </div>
                          
                          {/* Recent Tests */}
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-3">Recent Tests</h4>
                            <div className="space-y-2">
                              {recentTests.map((test, idx) => (
                                <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                  <div className="flex items-center gap-3">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                                      test.score >= 75 ? 'bg-green-100 text-green-700' :
                                      test.score >= 50 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                                    }`}>
                                      {test.score}%
                                    </div>
                                    <span className="text-sm text-gray-700">{test.test_title || 'Test'}</span>
                                  </div>
                                  <span className="text-xs text-gray-400">
                                    {test.completed_at ? format(new Date(test.completed_at), 'dd MMM') : '-'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                          
                          {/* Recommendation */}
                          <div className={`mt-4 p-4 rounded-lg ${
                            avgScore >= 75 ? 'bg-green-50 border border-green-200' :
                            avgScore >= 50 ? 'bg-yellow-50 border border-yellow-200' : 'bg-red-50 border border-red-200'
                          }`}>
                            <div className="flex items-start gap-3">
                              {avgScore >= 75 ? <Star className="text-green-500 flex-shrink-0" size={20} /> :
                               avgScore >= 50 ? <Zap className="text-yellow-500 flex-shrink-0" size={20} /> :
                               <AlertTriangle className="text-red-500 flex-shrink-0" size={20} />}
                              <div>
                                <p className={`font-medium ${
                                  avgScore >= 75 ? 'text-green-800' :
                                  avgScore >= 50 ? 'text-yellow-800' : 'text-red-800'
                                }`}>
                                  {avgScore >= 75 ? 'Excellent Performance!' :
                                   avgScore >= 50 ? 'Good Progress!' : 'Needs More Practice'}
                                </p>
                                <p className={`text-sm mt-1 ${
                                  avgScore >= 75 ? 'text-green-700' :
                                  avgScore >= 50 ? 'text-yellow-700' : 'text-red-700'
                                }`}>
                                  {avgScore >= 75 ? 'Keep up the great work! Try challenging questions to push further.' :
                                   avgScore >= 50 ? 'You\'re on the right track. Focus on weak areas and practice regularly.' :
                                   'Don\'t give up! Review the basics and try the Audio Learning feature for better understanding.'}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Empty state if no results */}
                  {approvedResults.length === 0 && (
                    <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                      <Target className="mx-auto text-indigo-300 mb-4" size={64} />
                      <h4 className="text-xl font-semibold text-gray-600">No Analysis Available</h4>
                      <p className="text-gray-400 mt-2">Complete tests to see detailed subject analysis</p>
                      <Button 
                        className="mt-4 bg-indigo-600 hover:bg-indigo-700"
                        onClick={() => setActiveSection('skill-assessment')}
                      >
                        Take a Test
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* ========== IMPROVEMENT VIEW (Phases 2/4/5) ========== */}
              {activeSubSection === 'improvement' && (
                <div className="space-y-6" data-testid="progress-improvement-view">
                  <ExamReadinessGauge />
                  <WeakTopicsList />
                  <StudentProgressWidget />
                </div>
              )}
            </div>
          )}

          {/* Online Classes */}
          {activeSection === 'online-classes' && (
            <div className="space-y-4">
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="px-4 md:px-6 py-4 border-b bg-gradient-to-r from-indigo-600 to-purple-600">
                  <h3 className="text-white font-semibold text-lg">Upcoming & Recent Classes</h3>
                  <p className="text-indigo-100 text-sm">Join your teacher's live classes</p>
                </div>
                {studentOnlineClasses.length === 0 ? (
                  <div className="p-8 text-center text-gray-400">
                    <Sparkles className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No online classes scheduled yet</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {studentOnlineClasses.map((oc, idx) => {
                      const classTime = new Date(oc.scheduled_at);
                      const now = new Date();
                      const isLive = classTime <= now && new Date(classTime.getTime() + oc.duration_minutes * 60000) >= now;
                      const isUpcoming = classTime > now;
                      const hasJoined = oc.attendees?.some(a => a.student_id === user?.student_id);
                      
                      return (
                        <div key={idx} className={`p-4 ${isLive ? 'bg-green-50 border-l-4 border-green-500' : ''}`} data-testid={`student-class-${idx}`}>
                          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h4 className="font-semibold text-gray-800">{oc.title}</h4>
                                {isLive && <Badge className="bg-green-500 text-white animate-pulse">LIVE NOW</Badge>}
                                {!isLive && isUpcoming && <Badge className="bg-blue-100 text-blue-700">Upcoming</Badge>}
                                {!isLive && !isUpcoming && <Badge className="bg-gray-200 text-gray-600">Completed</Badge>}
                              </div>
                              <p className="text-sm text-gray-600 mt-1">
                                <span className="font-medium">{oc.teacher_name}</span> | {oc.subject} - Class {oc.class_level}
                              </p>
                              {oc.topic && <p className="text-sm text-gray-500">{oc.topic}</p>}
                              <p className="text-xs text-gray-400 mt-1">
                                {classTime.toLocaleString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                {' '}({oc.duration_minutes} min)
                              </p>
                            </div>
                            <div className="flex-shrink-0">
                              {(isLive || isUpcoming) && (
                                <Button
                                  className={isLive ? "bg-green-600 hover:bg-green-700 animate-pulse" : "bg-indigo-600 hover:bg-indigo-700"}
                                  onClick={async () => {
                                    try {
                                      const res = await axios.post(`${API}/student/online-classes/${oc.id}/join`);
                                      if (res.data.meet_link) {
                                        window.open(res.data.meet_link, '_blank');
                                        toast.success('Attendance recorded! Joining class...');
                                      }
                                    } catch (error) {
                                      toast.error('Failed to join class');
                                    }
                                  }}
                                  data-testid={`join-class-${idx}`}
                                >
                                  {isLive ? 'Join Live Class' : 'Join When Live'}
                                </Button>
                              )}
                              {hasJoined && <Badge className="bg-green-100 text-green-600 mt-1">Attended</Badge>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}


          {/* Awards View */}
          {activeSection === 'awards' && (
            <StudentBadges />
          )}

          {/* My Fees View */}
          {activeSection === 'fees' && (
            <StudentFeesView />
          )}

          {/* Calendar View */}
          {activeSection === 'calendar' && (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
                <h3 className="text-white font-semibold text-lg flex items-center gap-2">
                  <Calendar size={20} />
                  Academic Calendar
                </h3>
              </div>
              <div className="p-8 text-center">
                <Calendar className="mx-auto text-blue-400 mb-4" size={64} />
                <h3 className="text-xl font-semibold text-gray-600">Coming Soon!</h3>
                <p className="text-gray-400 mt-2">View upcoming tests and events</p>
              </div>
            </div>
          )}

          {/* Homework View */}
          {activeSection === 'homework' && (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4">
                <h3 className="text-white font-semibold text-lg flex items-center gap-2">
                  <FileText size={20} />
                  My Homework
                </h3>
              </div>
              <div className="p-8 text-center">
                <FileText className="mx-auto text-orange-400 mb-4" size={64} />
                <h3 className="text-xl font-semibold text-gray-600">No Homework Assigned</h3>
                <p className="text-gray-400 mt-2">Your teachers haven't assigned any homework yet</p>
              </div>
            </div>
          )}

          {/* Audio Learning View */}
          {activeSection === 'audio' && (
            <AudioLearning onBack={() => setActiveSection('dashboard')} />
          )}

          {/* AI Tutor (Doubt Solver) */}
          {activeSection === 'doubt-solver' && (
            <DoubtSolver />
          )}

          {/* Announcements View */}
          {activeSection === 'announcements' && (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="bg-gradient-to-r from-pink-600 to-pink-700 px-6 py-4">
                <h3 className="text-white font-semibold text-lg flex items-center gap-2">
                  <Bell size={20} />
                  Announcements
                </h3>
              </div>
              <div className="p-8 text-center">
                <Bell className="mx-auto text-pink-400 mb-4" size={64} />
                <h3 className="text-xl font-semibold text-gray-600">No Announcements</h3>
                <p className="text-gray-400 mt-2">Check back later for updates from your school</p>
              </div>
            </div>
          )}

          {/* Mental Health View */}
          {activeSection === 'mental-health' && (
            <div className="space-y-6">
              {/* Daily Check-in Card */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-pink-500 to-rose-500 px-6 py-4">
                  <h3 className="text-white font-semibold text-lg flex items-center gap-2">
                    <Heart size={20} />
                    How Are You Feeling Today?
                  </h3>
                </div>
                
                {todayCheckin?.checked_in_today ? (
                  <div className="p-8 text-center">
                    <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-4">
                      <CheckCircle className="text-green-500" size={40} />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-700 mb-2">You've already checked in today!</h3>
                    <p className="text-gray-500 mb-4">
                      You reported feeling <span className="font-medium capitalize">{todayCheckin.checkin?.mood}</span> 
                      {' '}with stress level {todayCheckin.checkin?.stress_level}/5
                    </p>
                    <p className="text-sm text-gray-400">Come back tomorrow for your next check-in</p>
                  </div>
                ) : (
                  <div className="p-6">
                    {/* Mood Selection */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-3">Select your mood:</label>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        {[
                          { mood: 'happy', icon: Smile, label: 'Happy', color: 'green', bg: 'bg-green-50 hover:bg-green-100 border-green-200', active: 'bg-green-100 border-green-500' },
                          { mood: 'okay', icon: Meh, label: 'Okay', color: 'blue', bg: 'bg-blue-50 hover:bg-blue-100 border-blue-200', active: 'bg-blue-100 border-blue-500' },
                          { mood: 'sad', icon: Frown, label: 'Sad', color: 'yellow', bg: 'bg-yellow-50 hover:bg-yellow-100 border-yellow-200', active: 'bg-yellow-100 border-yellow-500' },
                          { mood: 'stressed', icon: AlertTriangle, label: 'Stressed', color: 'orange', bg: 'bg-orange-50 hover:bg-orange-100 border-orange-200', active: 'bg-orange-100 border-orange-500' },
                          { mood: 'anxious', icon: AlertCircle, label: 'Anxious', color: 'red', bg: 'bg-red-50 hover:bg-red-100 border-red-200', active: 'bg-red-100 border-red-500' }
                        ].map(({ mood, icon: Icon, label, bg, active }) => (
                          <button
                            key={mood}
                            onClick={() => setSelectedMood(mood)}
                            className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                              selectedMood === mood ? active : bg
                            }`}
                          >
                            <Icon size={32} className={selectedMood === mood ? 'text-gray-700' : 'text-gray-400'} />
                            <span className={`text-sm font-medium ${selectedMood === mood ? 'text-gray-700' : 'text-gray-500'}`}>
                              {label}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Stress Level */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Stress Level: <span className="text-lg font-bold text-teal-600">{stressLevel}/5</span>
                      </label>
                      <input
                        type="range"
                        min="1"
                        max="5"
                        value={stressLevel}
                        onChange={(e) => setStressLevel(parseInt(e.target.value))}
                        className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-teal-600"
                      />
                      <div className="flex justify-between text-xs text-gray-400 mt-1">
                        <span>Very Low</span>
                        <span>Low</span>
                        <span>Medium</span>
                        <span>High</span>
                        <span>Very High</span>
                      </div>
                    </div>

                    {/* Optional Note */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Want to share more? (Optional)
                      </label>
                      <textarea
                        value={mentalNote}
                        onChange={(e) => setMentalNote(e.target.value)}
                        placeholder="Write anything you'd like to share about how you're feeling..."
                        className="w-full p-3 border rounded-xl resize-none h-24 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      />
                    </div>

                    {/* Request to Talk */}
                    <div className="mb-6 p-4 bg-blue-50 rounded-xl">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={requestToTalk}
                          onChange={(e) => setRequestToTalk(e.target.checked)}
                          className="w-5 h-5 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                        />
                        <div>
                          <span className="font-medium text-gray-700 flex items-center gap-2">
                            <MessageCircle size={18} className="text-blue-500" />
                            I'd like to talk to someone
                          </span>
                          <p className="text-sm text-gray-500">A teacher will reach out to you privately</p>
                        </div>
                      </label>
                    </div>

                    {/* Submit Button */}
                    <Button
                      onClick={submitMentalHealthCheckin}
                      disabled={!selectedMood || submittingCheckin}
                      className="w-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white py-6 text-lg"
                    >
                      {submittingCheckin ? 'Submitting...' : 'Submit Check-in'}
                    </Button>
                  </div>
                )}
              </div>

              {/* Weekly History */}
              {checkinHistory.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-6 py-4">
                    <h3 className="text-white font-semibold text-lg">Your Week at a Glance</h3>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-7 gap-2">
                      {checkinHistory.slice(0, 7).reverse().map((checkin, index) => {
                        const moodColors = {
                          happy: 'bg-green-100 text-green-600',
                          okay: 'bg-blue-100 text-blue-600',
                          sad: 'bg-yellow-100 text-yellow-600',
                          stressed: 'bg-orange-100 text-orange-600',
                          anxious: 'bg-red-100 text-red-600'
                        };
                        const moodIcons = {
                          happy: Smile,
                          okay: Meh,
                          sad: Frown,
                          stressed: AlertTriangle,
                          anxious: AlertCircle
                        };
                        const MoodIcon = moodIcons[checkin.mood] || Meh;
                        
                        return (
                          <div key={index} className="text-center">
                            <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center ${moodColors[checkin.mood] || 'bg-gray-100'}`}>
                              <MoodIcon size={24} />
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              {format(new Date(checkin.created_at), 'EEE')}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Tips Card */}
              <div className="bg-gradient-to-r from-teal-500 to-teal-600 rounded-xl p-6 text-white">
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <Heart size={20} />
                  Mental Health Tips
                </h3>
                <ul className="space-y-2 text-sm opacity-90">
                  <li>• Take short breaks between study sessions</li>
                  <li>• Talk to a friend or family member when you feel low</li>
                  <li>• Get enough sleep - aim for 8-9 hours</li>
                  <li>• Exercise or play for at least 30 minutes daily</li>
                  <li>• Remember: It's okay to ask for help!</li>
                </ul>
              </div>
            </div>
          )}

          {/* Support View */}
          {activeSection === 'support' && (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="bg-gradient-to-r from-gray-600 to-gray-700 px-6 py-4">
                <h3 className="text-white font-semibold text-lg flex items-center gap-2">
                  <HelpCircle size={20} />
                  Support
                </h3>
              </div>
              <div className="p-8">
                <div className="max-w-md mx-auto text-center">
                  <HelpCircle className="mx-auto text-gray-400 mb-4" size={64} />
                  <h3 className="text-xl font-semibold text-gray-600 mb-4">Need Help?</h3>
                  <p className="text-gray-500 mb-6">Contact your teacher or school administrator for assistance</p>
                  <div className="bg-gray-50 rounded-lg p-4 text-left">
                    <p className="text-sm text-gray-600"><strong>Email:</strong> support@myclassai.net</p>
                    <p className="text-sm text-gray-600 mt-2"><strong>Hours:</strong> Mon-Fri, 9am-5pm</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* ========== PRE-EXAM SKILL ANALYSIS MODAL ========== */}
      {showPreExamAnalysis && preExamAnalysis && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="pre-exam-modal">
            {/* Header */}
            <div className={`p-6 text-white rounded-t-2xl ${
              preExamAnalysis.readiness_level === 'high' ? 'bg-gradient-to-r from-green-500 to-teal-500' :
              preExamAnalysis.readiness_level === 'medium' ? 'bg-gradient-to-r from-yellow-500 to-orange-500' :
              'bg-gradient-to-r from-red-500 to-pink-500'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold flex items-center gap-2">
                    <Brain size={24} />
                    Pre-Exam Skill Analysis
                  </h2>
                  <p className="text-white/80 text-sm mt-1">{preExamAnalysis.test_title}</p>
                </div>
                <button
                  onClick={() => { setShowPreExamAnalysis(false); setPreExamAnalysis(null); }}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Readiness Score */}
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Your Readiness Score</p>
                  <p className={`text-4xl font-bold ${
                    preExamAnalysis.readiness_level === 'high' ? 'text-green-600' :
                    preExamAnalysis.readiness_level === 'medium' ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {preExamAnalysis.readiness_score}%
                  </p>
                </div>
                <div className="text-right">
                  <div className="grid grid-cols-4 gap-2 text-center">
                    <div className="bg-red-50 p-2 rounded-lg">
                      <p className="text-lg font-bold text-red-600">{preExamAnalysis.skill_summary.weak}</p>
                      <p className="text-xs text-red-500">Weak</p>
                    </div>
                    <div className="bg-yellow-50 p-2 rounded-lg">
                      <p className="text-lg font-bold text-yellow-600">{preExamAnalysis.skill_summary.average}</p>
                      <p className="text-xs text-yellow-500">Average</p>
                    </div>
                    <div className="bg-green-50 p-2 rounded-lg">
                      <p className="text-lg font-bold text-green-600">{preExamAnalysis.skill_summary.strong}</p>
                      <p className="text-xs text-green-500">Strong</p>
                    </div>
                    <div className="bg-gray-50 p-2 rounded-lg">
                      <p className="text-lg font-bold text-gray-600">{preExamAnalysis.skill_summary.not_assessed}</p>
                      <p className="text-xs text-gray-500">New</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tips */}
            <div className="p-6 border-b">
              <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Lightbulb size={18} className="text-yellow-500" />
                Tips for This Exam
              </h3>
              <div className="space-y-2">
                {preExamAnalysis.tips?.map((tip, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-lg flex items-start gap-3 ${
                      tip.type === 'warning' ? 'bg-red-50 border border-red-100' :
                      tip.type === 'success' ? 'bg-green-50 border border-green-100' :
                      'bg-blue-50 border border-blue-100'
                    }`}
                  >
                    {tip.type === 'warning' && <AlertTriangle className="text-red-500 flex-shrink-0 mt-0.5" size={18} />}
                    {tip.type === 'success' && <Star className="text-green-500 flex-shrink-0 mt-0.5" size={18} />}
                    {tip.type === 'info' && <Lightbulb className="text-blue-500 flex-shrink-0 mt-0.5" size={18} />}
                    <p className="text-sm text-gray-700">{tip.message}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Weak & Strong Skills */}
            <div className="p-6 grid grid-cols-2 gap-4">
              {/* Weak Skills */}
              <div>
                <h4 className="font-medium text-red-600 mb-2 flex items-center gap-1">
                  <TrendingDown size={16} />
                  Focus Areas
                </h4>
                {preExamAnalysis.weak_skills?.length > 0 ? (
                  <div className="space-y-2">
                    {preExamAnalysis.weak_skills.map((skill, i) => (
                      <div key={i} className="bg-red-50 p-2 rounded-lg text-sm">
                        <p className="font-medium text-red-700">{skill.skill_name}</p>
                        <p className="text-xs text-red-500">{skill.chapter_name}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">No weak areas identified!</p>
                )}
              </div>

              {/* Strong Skills */}
              <div>
                <h4 className="font-medium text-green-600 mb-2 flex items-center gap-1">
                  <TrendingUp size={16} />
                  Strengths
                </h4>
                {preExamAnalysis.strong_skills?.length > 0 ? (
                  <div className="space-y-2">
                    {preExamAnalysis.strong_skills.map((skill, i) => (
                      <div key={i} className="bg-green-50 p-2 rounded-lg text-sm">
                        <p className="font-medium text-green-700">{skill.skill_name}</p>
                        <p className="text-xs text-green-500">{skill.chapter_name}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">Complete Skill Builder first</p>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="p-6 bg-gray-50 rounded-b-2xl flex gap-3">
              <Button
                variant="outline"
                onClick={() => { setShowPreExamAnalysis(false); setActiveSection('skills'); }}
                className="flex-1"
              >
                <Target className="mr-2" size={16} />
                Practice First
              </Button>
              <Button
                onClick={() => startTest(selectedTestForAnalysis)}
                className="flex-1 bg-teal-600 hover:bg-teal-700"
                data-testid="start-exam-btn"
              >
                <Play className="mr-2" size={16} />
                Start Exam Now
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ========== POST-EXAM REMEDIAL MODAL ========== */}
      {showPostExamRemedial && postExamRemedial && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" data-testid="post-exam-modal">
            {/* Header */}
            <div className={`p-6 text-white rounded-t-2xl ${
              postExamRemedial.priority === 'low' ? 'bg-gradient-to-r from-green-500 to-teal-500' :
              postExamRemedial.priority === 'medium' ? 'bg-gradient-to-r from-blue-500 to-indigo-500' :
              postExamRemedial.priority === 'high' ? 'bg-gradient-to-r from-yellow-500 to-orange-500' :
              'bg-gradient-to-r from-red-500 to-pink-500'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold flex items-center gap-2">
                    <GraduationCap size={24} />
                    Your Personalized Study Guide
                  </h2>
                  <p className="text-white/80 text-sm mt-1">{postExamRemedial.test_title}</p>
                </div>
                <button
                  onClick={() => { setShowPostExamRemedial(false); setPostExamRemedial(null); }}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Score Summary */}
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-20 h-20 rounded-full flex items-center justify-center text-white font-bold text-2xl ${
                    postExamRemedial.score?.percentage >= 70 ? 'bg-green-500' :
                    postExamRemedial.score?.percentage >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                  }`}>
                    {postExamRemedial.score?.percentage}%
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">{postExamRemedial.score?.correct} / {postExamRemedial.score?.total} Correct</p>
                    <p className="text-gray-500 text-sm">{postExamRemedial.message}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Weak Topics */}
            {postExamRemedial.weak_topics?.length > 0 && (
              <div className="p-6 border-b">
                <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
                  <AlertTriangle className="text-orange-500" size={18} />
                  Topics to Review
                </h3>
                <div className="space-y-4">
                  {postExamRemedial.weak_topics.map((topic, index) => (
                    <div key={index} className="bg-orange-50 border border-orange-100 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-orange-700">{topic.topic}</h4>
                        <Badge className="bg-orange-200 text-orange-700">
                          {topic.wrong_count} wrong
                        </Badge>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex gap-2 mt-3">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setShowPostExamRemedial(false); setActiveSection('audio'); }}
                          className="text-blue-600 border-blue-200 hover:bg-blue-50"
                        >
                          <Headphones size={14} className="mr-1" />
                          Audio Learning
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setShowPostExamRemedial(false); setActiveSection('skills'); }}
                          className="text-teal-600 border-teal-200 hover:bg-teal-50"
                        >
                          <Target size={14} className="mr-1" />
                          Skill Builder
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Strong Areas */}
            {postExamRemedial.strong_areas?.length > 0 && (
              <div className="p-6 border-b">
                <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Star className="text-green-500" size={18} />
                  Great Job On
                </h3>
                <div className="flex flex-wrap gap-2">
                  {postExamRemedial.strong_areas.map((area, index) => (
                    <Badge key={index} className="bg-green-100 text-green-700 px-3 py-1">
                      {area.topic} ({area.correct_count} correct)
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Next Steps */}
            <div className="p-6 border-b">
              <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Route size={18} className="text-purple-500" />
                Your Next Steps
              </h3>
              <div className="grid grid-cols-3 gap-3">
                {postExamRemedial.next_steps?.map((step, index) => (
                  <div key={index} className="bg-purple-50 p-3 rounded-lg text-center">
                    <div className="w-8 h-8 bg-purple-200 rounded-full flex items-center justify-center mx-auto mb-2 text-purple-700 font-bold">
                      {step.step}
                    </div>
                    <p className="text-xs text-purple-700">{step.action}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="p-6 bg-gray-50 rounded-b-2xl flex gap-3">
              <Button
                variant="outline"
                onClick={() => { setShowPostExamRemedial(false); setPostExamRemedial(null); }}
                className="flex-1"
              >
                Close
              </Button>
              <Button
                onClick={() => { setShowPostExamRemedial(false); setActiveSection('skills'); }}
                className="flex-1 bg-teal-600 hover:bg-teal-700"
              >
                <Target className="mr-2" size={16} />
                Start Practicing
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewStudentDashboard;
